import React, { useEffect, useRef, useState } from 'react';
import DateRangeCalendar, { nightsBetween, toISODate, type Selecting } from './DateRangeCalendar';

// "Book direct" inquiry: the guest picks dates on a calendar, sets the number
// of guests and leaves a name, email and phone. We reply with the direct rate.

interface SplashModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** 'page' renders the form as the /book page: no overlay, close button or scroll
   *  lock, and the form is split into three steps (dates → price → details). */
  variant?: 'modal' | 'page';
}

const MAX_GUESTS = 6;
// Screenshots are shrunk in the browser before sending, so a phone screenshot
// stays well under the email size limit while the prices remain readable.
const SHOT_MAX_SIDE = 2000;
const SHOT_QUALITY = 0.85;
const SHOT_MAX_BYTES = 3 * 1024 * 1024;

type Screenshot = { preview: string; base64: string; type: string; name: string };

const loadImage = (file: File) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('unreadable'));
    };
    img.src = url;
  });

const prepareScreenshot = async (file: File): Promise<Screenshot> => {
  if (!file.type.startsWith('image/')) throw new Error('not-image');
  const img = await loadImage(file);
  const scale = Math.min(1, SHOT_MAX_SIDE / Math.max(img.naturalWidth, img.naturalHeight));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(img.naturalWidth * scale);
  canvas.height = Math.round(img.naturalHeight * scale);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('unreadable');
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  const preview = canvas.toDataURL('image/jpeg', SHOT_QUALITY);
  const base64 = preview.slice(preview.indexOf(',') + 1);
  if (base64.length * 0.75 > SHOT_MAX_BYTES) throw new Error('too-big');
  const name = `${file.name.replace(/\.[^.]+$/, '') || 'airbnb-price'}.jpg`;
  return { preview, base64, type: 'image/jpeg', name };
};
const shortDate = (d: Date) => d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

const CalendarIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" aria-hidden="true">
    <rect x="3.5" y="5" width="17" height="15.5" rx="2.5" />
    <path d="M3.5 10h17M8 3v4M16 3v4" />
  </svg>
);

const SplashModal: React.FC<SplashModalProps> = ({ isOpen, onClose, variant = 'modal' }) => {
  const isPage = variant === 'page';
  const [checkIn, setCheckIn] = useState<Date | null>(null);
  const [checkOut, setCheckOut] = useState<Date | null>(null);
  const [selecting, setSelecting] = useState<Selecting>('start');
  const [calendarOpen, setCalendarOpen] = useState(true);
  const [guests, setGuests] = useState(2);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [futureStay, setFutureStay] = useState(false);
  const [screenshot, setScreenshot] = useState<Screenshot | null>(null);
  const [shotBusy, setShotBusy] = useState(false);
  const [shotError, setShotError] = useState('');
  const shotInputRef = useRef<HTMLInputElement>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const dialogRef = useRef<HTMLDivElement>(null);
  const lastFocus = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (isOpen && isPage) return undefined;
    if (isOpen) {
      lastFocus.current = document.activeElement as HTMLElement | null;
      document.body.style.overflow = 'hidden';
      dialogRef.current?.focus();

      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === 'Escape') onClose();
      };
      document.addEventListener('keydown', handleEscape);

      return () => {
        document.body.style.overflow = '';
        document.removeEventListener('keydown', handleEscape);
        lastFocus.current?.focus?.();
      };
    }
    document.body.style.overflow = '';
    // Start fresh next time.
    setCheckIn(null);
    setCheckOut(null);
    setSelecting('start');
    setCalendarOpen(true);
    setGuests(2);
    setFirstName('');
    setLastName('');
    setEmail('');
    setPhone('');
    setFutureStay(false);
    setScreenshot(null);
    setShotBusy(false);
    setShotError('');
    setSubmitStatus('idle');
    setErrorMessage('');
    return undefined;
  }, [isOpen, isPage, onClose]);

  const nights = checkIn && checkOut ? nightsBetween(checkIn, checkOut) : 0;
  // In the modal every part shows at once; on the /book page one step at a time.
  const showStep = (n: 1 | 2 | 3) => !isPage || step === n;

  const goToStep = (n: 1 | 2 | 3) => {
    setStep(n);
    setErrorMessage('');
    setSubmitStatus('idle');
    window.requestAnimationFrame?.(() => {
      const top = dialogRef.current?.getBoundingClientRect().top ?? 0;
      if (top < 0) dialogRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      dialogRef.current?.querySelector<HTMLElement>('.booking-step-title')?.focus({ preventScroll: true });
    });
  };

  const next = () => {
    if (step === 1 && (!checkIn || !checkOut)) {
      setSubmitStatus('error');
      setErrorMessage('Pick your check-in and check-out dates first.');
      openCalendar(checkIn ? 'end' : 'start');
      return;
    }
    if (step < 3) goToStep((step + 1) as 2 | 3);
  };

  const openCalendar = (which: Selecting) => {
    setSelecting(which === 'end' && !checkIn ? 'start' : which);
    setCalendarOpen(true);
  };

  const onScreenshot = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setShotError('');
    setShotBusy(true);
    try {
      setScreenshot(await prepareScreenshot(file));
    } catch (error) {
      const reason = error instanceof Error ? error.message : '';
      setShotError(
        reason === 'not-image'
          ? 'Please choose an image (a screenshot or photo).'
          : reason === 'too-big'
            ? 'That image is too large — try a regular screenshot.'
            : 'We couldn’t read that image. Try a screenshot saved as JPG or PNG.',
      );
    } finally {
      setShotBusy(false);
    }
  };

  const onDates = (start: Date | null, end: Date | null, done: boolean) => {
    setCheckIn(start);
    setCheckOut(end);
    if (errorMessage) setErrorMessage('');
    if (done) {
      setSelecting('start');
      // Let the range paint before folding the calendar away.
      window.setTimeout(() => setCalendarOpen(false), 280);
    } else {
      setSelecting('end');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Enter on an earlier step moves forward instead of sending.
    if (isPage && step < 3) {
      next();
      return;
    }
    if (!checkIn || !checkOut) {
      if (isPage) goToStep(1);
      setSubmitStatus('error');
      setErrorMessage('Pick your check-in and check-out dates first.');
      openCalendar(checkIn ? 'end' : 'start');
      return;
    }
    if (phone.replace(/\D/g, '').length < 7) {
      setSubmitStatus('error');
      setErrorMessage('Please add a phone number we can text or call.');
      document.getElementById('bookingPhone')?.focus();
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');

    try {
      const response = await fetch('/api/guests', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: firstName,
          last_name: lastName,
          email,
          phone,
          check_in: toISODate(checkIn),
          check_out: toISODate(checkOut),
          guests,
          future_stay: futureStay,
          ...(screenshot ? { rate_screenshot: { data: screenshot.base64, type: screenshot.type, name: screenshot.name } } : {}),
          signup_source: 'booking_inquiry',
        }),
      });
      const data = await response.json();

      if (response.ok && data.success) {
        setSubmitStatus('success');
      } else {
        setSubmitStatus('error');
        setErrorMessage(data.error || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      setSubmitStatus('error');
      setErrorMessage('Network error. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div id="splashModal" className={`splash-modal active booking-modal${isPage ? ' booking-page' : ''}`}>
      {!isPage && <div className="splash-modal-overlay" onClick={onClose} />}
      <div
        className="splash-modal-content booking-sheet"
        role={isPage ? undefined : 'dialog'}
        aria-modal={isPage ? undefined : true}
        aria-labelledby="bookingTitle"
        tabIndex={-1}
        ref={dialogRef}
      >
        {!isPage && (
          <button type="button" className="splash-modal-close booking-close" aria-label="Close" onClick={onClose}>
            &times;
          </button>
        )}

        {submitStatus === 'success' && checkIn && checkOut ? (
          <div className="booking-done" role="status">
            <span className="booking-done-mark" aria-hidden="true">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="m5 12.5 4.5 4.5L19 7.5" /></svg>
            </span>
            <p className="booking-eyebrow">Request sent</p>
            <h2 id="bookingTitle">Thanks, {firstName}!</h2>
            <p>
              We&rsquo;ll check <strong>{shortDate(checkIn)} – {shortDate(checkOut)}</strong> for {guests}{' '}
              {guests === 1 ? 'guest' : 'guests'} and send your direct rate to <strong>{email}</strong>, usually within a day.
            </p>
            <p className="booking-done-note">Your current reservation isn&rsquo;t affected — keep using the Airbnb app for this stay.</p>
            {!isPage && (
              <button type="button" className="booking-submit" onClick={onClose}>
                Back to the guide
              </button>
            )}
          </div>
        ) : (
          <>
            {!isPage && (
            <header className="booking-head">
              <p className="booking-eyebrow">Your next stay</p>
              <h2 id="bookingTitle">
                Save 15% off <em>Airbnb rates</em>
              </h2>
              <p className="booking-policy">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 8h.01" /></svg>
                <span>
                  <strong>For future stays only.</strong> Your current reservation stays on Airbnb — for anything about it, message us in the Airbnb app.
                </span>
              </p>
            </header>
            )}

            <form onSubmit={handleSubmit} className="booking-form" noValidate={isPage && step < 3}>
              {isPage && (
                <>
                  <ol className="booking-progress" aria-label="Steps">
                    {(['Dates', 'Price', 'You'] as const).map((label, i) => {
                      const n = (i + 1) as 1 | 2 | 3;
                      const state = n < step ? 'is-done' : n === step ? 'is-current' : '';
                      return (
                        <li key={label} className={state} aria-current={n === step ? 'step' : undefined}>
                          {n < step ? (
                            <button type="button" onClick={() => goToStep(n)} aria-label={`Back to step ${n}: ${label}`}>
                              <span className="booking-progress-dot" aria-hidden="true">✓</span>
                              <span className="booking-progress-label">{label}</span>
                            </button>
                          ) : (
                            <>
                              <span className="booking-progress-dot" aria-hidden="true">{n}</span>
                              <span className="booking-progress-label">{label}</span>
                            </>
                          )}
                        </li>
                      );
                    })}
                  </ol>
                  <h2 id="bookingTitle" className="booking-step-title" tabIndex={-1}>
                    {step === 1
                      ? 'When would you like to stay?'
                      : step === 2
                        ? 'What did Airbnb show you?'
                        : 'Where should we send your rate?'}
                  </h2>
                </>
              )}
              {showStep(1) && (
              <>
              <fieldset className="booking-dates">
                <legend className="booking-label">Your dates</legend>
                <div className="booking-date-pair">
                  <button
                    type="button"
                    className={`booking-date ${calendarOpen && selecting === 'start' ? 'is-active' : ''}`}
                    aria-expanded={calendarOpen}
                    aria-controls="bookingCalendar"
                    onClick={() => (calendarOpen && selecting === 'start' ? setCalendarOpen(false) : openCalendar('start'))}
                  >
                    <CalendarIcon />
                    <span>
                      <small>Check-in</small>
                      <strong className={checkIn ? '' : 'is-empty'}>{checkIn ? shortDate(checkIn) : 'Add date'}</strong>
                    </span>
                  </button>
                  <span className="booking-date-arrow" aria-hidden="true">→</span>
                  <button
                    type="button"
                    className={`booking-date ${calendarOpen && selecting === 'end' ? 'is-active' : ''}`}
                    aria-expanded={calendarOpen}
                    aria-controls="bookingCalendar"
                    onClick={() => (calendarOpen && selecting === 'end' ? setCalendarOpen(false) : openCalendar('end'))}
                  >
                    <CalendarIcon />
                    <span>
                      <small>Check-out</small>
                      <strong className={checkOut ? '' : 'is-empty'}>{checkOut ? shortDate(checkOut) : 'Add date'}</strong>
                    </span>
                  </button>
                </div>

                <div className={`booking-cal-wrap ${calendarOpen ? 'is-open' : ''}`} hidden={!calendarOpen}>
                  {calendarOpen && (
                    <DateRangeCalendar id="bookingCalendar" start={checkIn} end={checkOut} selecting={selecting} onChange={onDates} />
                  )}
                </div>

                {nights > 0 && !calendarOpen && (
                  <p className="booking-nights">
                    {nights} {nights === 1 ? 'night' : 'nights'}
                    <button type="button" onClick={() => openCalendar('start')}>Change dates</button>
                  </p>
                )}
              </fieldset>

              <div className="booking-guests">
                <div>
                  <span className="booking-label" id="guestsLabel">Guests</span>
                  <small>Up to {MAX_GUESTS} · no pets</small>
                </div>
                <div className="booking-stepper" role="group" aria-labelledby="guestsLabel">
                  <button type="button" aria-label="Fewer guests" onClick={() => setGuests((g) => Math.max(1, g - 1))} disabled={guests <= 1}>
                    −
                  </button>
                  <output aria-live="polite" aria-label={`${guests} ${guests === 1 ? 'guest' : 'guests'}`}>{guests}</output>
                  <button type="button" aria-label="More guests" onClick={() => setGuests((g) => Math.min(MAX_GUESTS, g + 1))} disabled={guests >= MAX_GUESTS}>
                    +
                  </button>
                </div>
              </div>
              </>
              )}

              {showStep(2) && (
              <div className="booking-shot">
                <span className="booking-label" id="shotLabel">Airbnb price details</span>
                <div className="booking-shot-guide">
                  <p id="shotSteps" className="booking-shot-ask">
                    Please add a screenshot of the <strong>Price details</strong> you saw on Airbnb for{' '}
                    {isPage && checkIn && checkOut ? `${shortDate(checkIn)} – ${shortDate(checkOut)}` : 'these dates'}.
                  </p>
                  <figure className="booking-shot-example">
                    <figcaption>Example</figcaption>
                    <img
                      src="images/airbnb-price-details-example.webp"
                      alt="Example screenshot: Airbnb Price details showing 4 nights x $277.00 = $1,108.00 for Nov 2 – 6"
                      width={600}
                      height={313}
                      loading="lazy"
                    />
                  </figure>
                </div>
                {screenshot ? (
                  <div className="booking-shot-file">
                    <img src={screenshot.preview} alt="Your Airbnb price screenshot" />
                    <div>
                      <strong>Price details added</strong>
                      <small>{screenshot.name}</small>
                    </div>
                    <button type="button" onClick={() => setScreenshot(null)} disabled={isSubmitting}>
                      Remove
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="booking-shot-drop"
                    aria-describedby="shotSteps shotHint"
                    onClick={() => shotInputRef.current?.click()}
                    disabled={shotBusy || isSubmitting}
                  >
                    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <rect x="3" y="4" width="18" height="16" rx="3" />
                      <circle cx="9" cy="10" r="1.8" />
                      <path d="m21 16-5-5-8 8" />
                    </svg>
                    <span>
                      <strong>{shotBusy ? 'Adding…' : 'Add your price-details screenshot'}</strong>
                      <small id="shotHint">Optional — we&rsquo;ll take 15% off that total.</small>
                    </span>
                  </button>
                )}
                <input
                  ref={shotInputRef}
                  type="file"
                  accept="image/*"
                  className="sr-only"
                  tabIndex={-1}
                  aria-labelledby="shotLabel"
                  onChange={onScreenshot}
                />
                {shotError && <p className="booking-shot-error" role="alert">{shotError}</p>}
              </div>
              )}

              {showStep(3) && (
              <>
              <div className="booking-fields">
                <label className="booking-field">
                  <span>First name</span>
                  <input type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} required disabled={isSubmitting} autoComplete="given-name" placeholder="Alex" />
                </label>
                <label className="booking-field">
                  <span>Last name</span>
                  <input type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} required disabled={isSubmitting} autoComplete="family-name" placeholder="Rivera" />
                </label>
                <label className="booking-field">
                  <span>Email</span>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required disabled={isSubmitting} autoComplete="email" inputMode="email" placeholder="alex@example.com" />
                </label>
                <label className="booking-field">
                  <span>Phone</span>
                  <input id="bookingPhone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} required disabled={isSubmitting} autoComplete="tel" inputMode="tel" placeholder="(206) 555-0123" />
                </label>
              </div>

              <label className="booking-confirm">
                <input type="checkbox" checked={futureStay} onChange={(e) => setFutureStay(e.target.checked)} required disabled={isSubmitting} />
                <span>These dates are for a future trip, separate from any current Airbnb reservation.</span>
              </label>
              </>
              )}

              {submitStatus === 'error' && errorMessage && (
                <p className="booking-error" role="alert">{errorMessage}</p>
              )}

              {isPage && step < 3 ? (
                <button type="submit" className="booking-submit">
                  {step === 1 ? 'Next: Airbnb price' : screenshot ? 'Next: your details' : 'Skip for now'}{' '}
                  <span aria-hidden="true">→</span>
                </button>
              ) : (
                <button type="submit" className="booking-submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Sending…' : 'Request my direct rate'}
                </button>
              )}
              {isPage && step > 1 && (
                <button type="button" className="booking-back" onClick={() => goToStep((step - 1) as 1 | 2)} disabled={isSubmitting}>
                  <span aria-hidden="true">←</span> Back
                </button>
              )}
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default SplashModal;
